/*
 * Copyright (c) 2025 BuyerView.ai
 * All rights reserved.
 * Unauthorized copying prohibited.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';
import { getPostBySlug, type BlogPost as BlogPostData, type BlogFAQ } from '../utils/blogLoader';

const SITE_URL = 'https://buyerview.ai';
const SITE_NAME = 'BuyerView';
const TWITTER_HANDLE = '@buyerview_ai';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og/default.png`;
const ORG_LOGO = `${SITE_URL}/logo.png`;

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

function toIsoDateTime(iso: string): string {
  if (!iso) return '';
  // Frontmatter dates are like "2026-05-18" — emit AEST midnight ISO so social platforms parse cleanly.
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return `${iso}T08:00:00+10:00`;
  return iso;
}

function absoluteUrl(maybeRelative: string | undefined, fallback: string): string {
  if (!maybeRelative) return fallback;
  if (/^https?:\/\//i.test(maybeRelative)) return maybeRelative;
  return `${SITE_URL}${maybeRelative.startsWith('/') ? '' : '/'}${maybeRelative}`;
}

/**
 * Iframe renderer for blog markdown. Built for the marketing-team visual files
 * which postMessage their card height up. Two extra behaviours:
 *   - If `data-png` is supplied, shows the static PNG until the iframe is near
 *     the viewport (intersection observer). Then swaps in the live iframe.
 *     This keeps LCP fast on the blog page and gives search/social crawlers
 *     a real image they can index even though they don't execute the swap.
 *   - Listens for `bv-iframe-height` postMessage from the framed page and
 *     resizes itself accordingly — matches the contract the standalone
 *     visual HTML files already implement.
 */
type BlogIframeProps = React.IframeHTMLAttributes<HTMLIFrameElement> & {
  'data-png'?: string;
  'data-png-alt'?: string;
};

function BlogIframe(props: BlogIframeProps) {
  const { src, title, height, ...rest } = props;
  const pngSrc = props['data-png'];
  const pngAlt = props['data-png-alt'] || title || 'Visual';
  const initialHeight = typeof height === 'number' ? height : parseInt(String(height || '800'), 10) || 800;

  const [showIframe, setShowIframe] = useState<boolean>(!pngSrc);
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Defer iframe load until the PNG enters the viewport (with rootMargin so it
  // starts a bit before, hiding the swap).
  useEffect(() => {
    if (!pngSrc) return;
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setShowIframe(true);
      return;
    }
    const io = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          setShowIframe(true);
          io.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [pngSrc]);

  // Receive height updates from the framed page.
  useEffect(() => {
    if (!showIframe) return;
    function onMessage(e: MessageEvent) {
      const data = e.data;
      if (!data || data.type !== 'bv-iframe-height') return;
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow === e.source && typeof data.height === 'number') {
        setMeasuredHeight(data.height);
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [showIframe]);

  const renderedHeight = measuredHeight ?? initialHeight;

  // Strip data-* attributes from the spread to avoid passing unknown props to iframe.
  const iframeProps: React.IframeHTMLAttributes<HTMLIFrameElement> = {};
  Object.entries(rest).forEach(([k, v]) => {
    if (!k.startsWith('data-')) (iframeProps as Record<string, unknown>)[k] = v;
  });

  return (
    <div
      ref={containerRef}
      className="my-6 rounded-lg overflow-hidden border border-gray-200 bg-white"
    >
      {showIframe ? (
        <iframe
          {...iframeProps}
          ref={iframeRef}
          src={src}
          title={title}
          loading="lazy"
          scrolling="no"
          style={{ width: '100%', border: 'none', display: 'block', height: renderedHeight }}
        />
      ) : (
        <img
          src={pngSrc}
          alt={pngAlt}
          loading="lazy"
          className="w-full block"
          style={{ height: 'auto' }}
        />
      )}
    </div>
  );
}

const markdownComponents = {
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-8 mb-4 leading-tight">{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-8 mb-3 leading-snug">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mt-6 mb-2">{children}</h3>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4">{children}</p>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="list-disc list-outside ml-5 sm:ml-6 mb-4 space-y-2 text-gray-700 text-base sm:text-lg">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="list-decimal list-outside ml-5 sm:ml-6 mb-4 space-y-2 text-gray-700 text-base sm:text-lg">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="leading-relaxed">{children}</li>
  ),
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a href={href} className="text-[#4ECDC4] underline hover:text-[#3DB8B0]" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <blockquote className="border-l-4 border-[#4ECDC4] pl-4 italic text-gray-600 my-4">{children}</blockquote>
  ),
  code: ({ children }: { children?: React.ReactNode }) => (
    <code className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
  ),
  pre: ({ children }: { children?: React.ReactNode }) => (
    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto my-4 text-sm">{children}</pre>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold text-gray-900">{children}</strong>
  ),
  hr: () => <hr className="my-8 border-gray-200" />,
  iframe: (props: BlogIframeProps) => <BlogIframe {...props} />,
};

/**
 * Builds JSON-LD @graph (BlogPosting + Person + Organization + BreadcrumbList +
 * optional FAQPage) from the post's frontmatter and extracted FAQs.
 */
function buildJsonLd(post: BlogPostData): Record<string, unknown> {
  const canonicalUrl = post.canonicalUrl || `${SITE_URL}/blog/${post.slug}`;
  const imageUrl = absoluteUrl(post.image, DEFAULT_OG_IMAGE);
  const published = toIsoDateTime(post.date);
  const modified = toIsoDateTime(post.modifiedDate || post.date);
  const description = post.description || post.excerpt;

  const personId = `${SITE_URL}/#author-${post.slug}`;
  const orgId = `${SITE_URL}/#org`;

  const graph: Record<string, unknown>[] = [
    {
      '@type': 'BlogPosting',
      '@id': `${canonicalUrl}#article`,
      headline: post.title,
      description,
      datePublished: published,
      dateModified: modified,
      inLanguage: 'en-AU',
      isAccessibleForFree: true,
      ...(post.section ? { articleSection: post.section } : {}),
      ...(post.keywords ? { keywords: post.keywords } : {}),
      author: { '@id': personId },
      publisher: { '@id': orgId },
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
      image: imageUrl,
    },
    {
      '@type': 'Person',
      '@id': personId,
      name: post.author,
      ...(post.authorUrl ? { url: absoluteUrl(post.authorUrl, `${SITE_URL}/about`) } : {}),
      worksFor: { '@id': orgId },
    },
    {
      '@type': 'Organization',
      '@id': orgId,
      name: SITE_NAME,
      url: SITE_URL,
      logo: { '@type': 'ImageObject', url: ORG_LOGO },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
        { '@type': 'ListItem', position: 3, name: post.title },
      ],
    },
  ];

  if (post.faqs.length > 0) {
    graph.push({
      '@type': 'FAQPage',
      mainEntity: post.faqs.map((f: BlogFAQ) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    });
  }

  return { '@context': 'https://schema.org', '@graph': graph };
}

function BlogPostSeo({ post }: { post: BlogPostData }) {
  const canonicalUrl = post.canonicalUrl || `${SITE_URL}/blog/${post.slug}`;
  const imageUrl = absoluteUrl(post.image, DEFAULT_OG_IMAGE);
  const description = post.description || post.excerpt;
  const published = toIsoDateTime(post.date);
  const modified = toIsoDateTime(post.modifiedDate || post.date);
  const jsonLd = buildJsonLd(post);

  return (
    <Helmet>
      <html lang="en-AU" />
      <title>{`${post.title} · ${SITE_NAME}`}</title>
      <link rel="canonical" href={canonicalUrl} />
      <meta name="description" content={description} />
      <meta name="author" content={post.author} />
      <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1" />
      {post.keywords && <meta name="keywords" content={post.keywords} />}

      {/* Open Graph */}
      <meta property="og:type" content="article" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={post.title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={post.imageAlt || post.title} />
      <meta property="og:locale" content="en_AU" />
      <meta property="article:published_time" content={published} />
      <meta property="article:modified_time" content={modified} />
      <meta property="article:author" content={post.author} />
      {post.section && <meta property="article:section" content={post.section} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={post.title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={post.imageAlt || post.title} />

      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
}

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!post) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <Helmet>
          <title>{`Post not found · ${SITE_NAME}`}</title>
          <meta name="robots" content="noindex,follow" />
        </Helmet>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Post not found</h1>
        <p className="text-gray-600 mb-6">The article you're looking for doesn't exist or may have been moved.</p>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#4ECDC4] text-white rounded-lg hover:bg-[#3DB8B0] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to blog
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 sm:py-10">
      <BlogPostSeo post={post} />
      <div className="max-w-3xl mx-auto">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#4ECDC4] mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          All posts
        </Link>

        <article>
          {/* Hero image — 1200×630 landscape (Open Graph ratio) */}
          <div className="mb-6 sm:mb-8 rounded-xl overflow-hidden bg-gradient-to-br from-[#4ECDC4]/20 to-[#0A2342]/20 aspect-[1200/630]">
            {post.image ? (
              <img
                src={post.image}
                alt={post.imageAlt || post.title}
                width={1200}
                height={630}
                loading="eager"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                <span>1200 × 630 hero image</span>
              </div>
            )}
          </div>

          <header className="mb-6 sm:mb-8 pb-6 border-b border-gray-200">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-4">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
              {post.date && (
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" />
                  <time dateTime={post.date}>{formatDate(post.date)}</time>
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <Clock className="h-4 w-4" />
                {post.readingTime}
              </span>
              <span>
                by{' '}
                {post.authorUrl ? (
                  <a
                    rel="author"
                    href={post.authorUrl}
                    className="text-gray-700 hover:text-[#4ECDC4]"
                  >
                    {post.author}
                  </a>
                ) : (
                  post.author
                )}
              </span>
            </div>
          </header>

          <div className="blog-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={markdownComponents}
            >
              {post.content}
            </ReactMarkdown>
          </div>
        </article>
      </div>
    </div>
  );
}
